<template>
  <div class="gwp-facade">
    <span :class="['gwp-facade__title', 'margin-bottom-xs', { h4: isGWPEnabled2 }]" data-test-id="gwp-facade-title">
      {{ title }}
    </span>
    <GWPExpandableList
      class="expandable-list-container--gwp"
      :class="{
        'expandable-list-container--more-than-two': promotions.length > 2,
      }"
      :items="promotions"
    >
      <template #item="{ item: promotion }">
        <GWPProductItem
          v-bind="{ ...getPropsByPromoId(promotion.promoId) }"
          :is-loading="isGiftInBagStatusLoading"
          @open-quick-view="handleOpenQuickView(promotion)"
        >
          <template #swap-button>
            <SwapButton v-bind="{ ...getSwapButtonProps(promotion) }" />
          </template>
        </GWPProductItem>
      </template>
    </GWPExpandableList>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch, watchEffect } from 'vue';
import useGWPStore from '~/stores/useGWPStore';
import { useMounted } from '@vueuse/core';
import { sanitize } from '@vue-util/sanitize';

import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useProductQuickviewStore } from '~/stores/useProductQuickviewStore';
import useExperiments from '~/stores/useExperimentsStore';

import GWPProductItem from './ProductItem.vue';
import GWPExpandableList from './ExpandableList.vue';
import SwapButton from './swap/SwapButton.vue';
import type { Promotion, GiftProduct, PropsByPromoId } from '../types';

const store = useGWPStore();
const { productId } = useProduct();
const selectedPrice = useProductPrice();
const selectedMemberId = useSelectedMemberId();
const isMounted = useMounted();
const { setPdpPqvPayload, setPdpPqvOptions } = useProductQuickviewStore();
const { isBigTicketItemFlag } = useProductFlags();
const {
  experimentFlags: { isGWPEnabled2 },
} = useExperiments();

const isGiftInBagStatusLoading = computed(() => store.isGiftInBagStatusLoading);
const promotions = computed(() => store.getPromotions);

watchEffect(() => {
  const selectedProductId = selectedMemberId.value || productId;
  store.setSelectedProductId(selectedProductId);
});

watch(
  () => store.selectedProductId,
  async () => {
    store.initializeGwp({
      promotions: selectedPrice.value.gwpPromotions,
    });

    if (!isMounted.value) return;

    await store.fetchGiftInBagStatus();

    store.preselectUpcForEveryPromotion();
  },
  { immediate: true },
);

onMounted(async () => {
  await store.fetchGiftInBagStatus();

  store.preselectUpcForEveryPromotion();
});

const title = computed(() => {
  const count = promotions.value.length;
  const separator = isGWPEnabled2 ? '' : ':';

  const areAllThresholdsMet = promotions.value.every((promo) => store.isThresholdMet(promo.promoId, selectedPrice.value.lowestSalePriceTier));

  if (areAllThresholdsMet) {
    const text = count > 1 ? `Enjoy ${count} gifts with purchase` : 'Enjoy a gift with purchase';
    return `${text}${separator}`;
  }

  const suffix = count === 1 ? 'offer' : 'offers';
  return `${count} gift ${suffix}${separator}`;
});

function getGiftProduct(promotion: Promotion): GiftProduct | undefined {
  return Object.values(promotion.giftProductsById)[0];
}

function handleOpenQuickView(promotion: Promotion): void {
  const giftProduct = getGiftProduct(promotion);

  if (!giftProduct || !giftProduct.id) return;

  const { id: giftProductId, alreadyInBag, giftSelectedUpcId } = giftProduct;

  setPdpPqvOptions({
    productId: giftProductId,
    isGWP: true,
    isNotificationAvailable: !isBigTicketItemFlag,
    selectedUpcId: giftSelectedUpcId,
    callbackOnClose: (selectedUpcId) => {
      if (alreadyInBag) return;

      store.setSelectedUpcId({
        promoId: promotion.promoId,
        giftProductId,
        giftSelectedUpcId: selectedUpcId,
        alreadyInBag,
      });
    },
  });

  setPdpPqvPayload({
    productId: giftProductId,
    slideoutTitle: 'Gift with purchase',
  });
}

function getPropsByPromoId(promoId: Promotion['promoId']): PropsByPromoId {
  return store.getPropsByPromoId(promoId, selectedPrice.value.lowestSalePriceTier);
}

function getSwapButtonProps(promotion: Promotion): {
  promoId: Promotion['promoId'];
  productId: GiftProduct['id'];
  productUrl?: string;
} {
  const giftProduct = getGiftProduct(promotion);

  return {
    promoId: promotion.promoId,
    productId: giftProduct?.id || '',
    productUrl: sanitize(giftProduct?.identifier?.productUrl || ''),
  };
}
</script>




<style lang="scss" scoped>
@import '~settingSCSS';
@import '@csgIcons/new/chevron-large-right-dark-small.scss';

$box-shadow-color: 0px 0px 8px 0px rgba(0, 0, 0, 0.1);

.gwp-facade__title {
  display: block;
}

:deep() {
  .expandable-list-container--gwp {
    .expandable-list__toggle {
      position: absolute;
      bottom: 0;
      left: calc(50% - 16px);
      z-index: 3;
      width: 32px;
      height: 32px;
      background-color: white;
      border-radius: 50%;
      padding: rem-calc(4);
      cursor: pointer;
    }

    &.expandable-list-container--expandable {
      &.expandable-list-container--opened {
        margin-bottom: rem-calc(60) !important;

        .expandable-list__toggle {
          bottom: -36px;
        }
      }

      &.expandable-list-container--closed {
        @include margin-bottom-xl;

        &::after {
          content: '';
          position: absolute;
          bottom: -12px;
          left: 8px;
          right: 8px;
          height: 16px;
          background-color: $background-color;
          z-index: 1;
          border-radius: 5px;
          box-shadow: $box-shadow-color;
        }

        &.expandable-list-container--more-than-two {
          &::after {
            bottom: -8px;
            z-index: 1;
          }

          &::before {
            content: '';
            position: absolute;
            bottom: -16px;
            left: 16px;
            right: 16px;
            height: 16px;
            background-color: $background-color;
            z-index: 1;
            border-radius: 5px;
            box-shadow: $box-shadow-color;
          }
        }

        .expandable-list__toggle {
          bottom: -24px;
        }

        .expandable-list__item {
          position: relative;
          border-radius: 5px;
          z-index: 2;

          &:first-child {
            box-shadow: $box-shadow-color;
          }
        }
      }
    }
  }
}


@import '~settingSCSS';

$box-shadow-color: 1px 1px 2px 0px rgba(0, 0, 0, 0.1);

:deep() {
  .expandable-list-container--gwp {
    .expandable-list__toggle {
      background-color: $white-overlay-color-2;
    }

    &.expandable-list-container--expandable {
      &.expandable-list-container--opened {
        margin-bottom: rem-calc(72) !important;

        .expandable-list__toggle {
          bottom: -40px;
        }
      }

      &.expandable-list-container--closed {
        margin-bottom: rem-calc(52) !important;

        &::after {
          border-radius: 0px;
          box-shadow: $box-shadow-color;
        }

        &.expandable-list-container--more-than-two {
          &::before {
            border-radius: 0px;
            box-shadow: $box-shadow-color;
          }
        }

        .expandable-list__item {
          border-radius: 0px;

          &:first-child {
            box-shadow: $box-shadow-color;
          }
        }
      }
    }
  }
}
</style>